import { useState } from "react";
import type { ClaimsVolumeSeries, ClaimsVolumeSourceChoice } from "@broadview/api-client";
import { useClaimsVolumeTrendQuery } from "@/lib/portal-queries";
import { claimsPeriodAxisUtc, claimsPeriodStartUtc } from "@/lib/claims-trend-presentation";
import { ChartDataTable } from "./ChartDataTable";
import {
  Area,
  ComposedChart,
  CartesianGrid,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

function tone(series: ClaimsVolumeSeries | undefined) {
  return series?.dataState === "SYNTHETIC"
    ? "border-warning/30 bg-warning/10 text-warning"
    : series?.status === "AVAILABLE"
      ? "border-success/30 bg-success/10 text-success"
      : "border-border bg-muted text-muted-foreground";
}

export function ClaimsVolumeTrend() {
  const [sourceChoice, setSourceChoice] = useState<ClaimsVolumeSourceChoice>("SOURCE_RANKED");
  const query = useClaimsVolumeTrendQuery(sourceChoice);
  const sourceControl = (
    <label className="mt-3 block text-xs text-muted-foreground">
      Claims trend source
      <select
        aria-label="Claims trend source"
        value={sourceChoice}
        onChange={(event) => setSourceChoice(event.target.value as ClaimsVolumeSourceChoice)}
        className="ml-2 max-w-full rounded-md border border-border bg-background px-2 py-2 text-foreground focus-visible:ring-2 focus-visible:ring-ring"
      >
        <option value="SOURCE_RANKED">Source-ranked data (default)</option>
        <option value="GOVERNED_DEMONSTRATION">Governed synthetic demonstration</option>
      </select>
      {sourceChoice === "GOVERNED_DEMONSTRATION" && (
        <span className="mt-2 block text-warning">
          Demonstration only — never combined with historical actuals; production decision use
          prohibited.
        </span>
      )}
    </label>
  );

  if (query.isPending) {
    return (
      <section
        aria-label="Claims volume trend"
        className="min-w-0 rounded-xl border border-border p-5 lg:col-span-2"
        style={{ background: "var(--gradient-card)", boxShadow: "var(--shadow-card)" }}
      >
        <h2 className="text-sm font-semibold">Claims Volume Trend</h2>
        {sourceControl}
        <p className="mt-3 text-xs text-muted-foreground">Loading governed claim records…</p>
      </section>
    );
  }

  if (query.isError) {
    return (
      <section
        aria-label="Claims volume trend unavailable"
        className="min-w-0 rounded-xl border border-border p-5 lg:col-span-2"
        style={{ background: "var(--gradient-card)", boxShadow: "var(--shadow-card)" }}
      >
        <h2 className="text-sm font-semibold">Claims Volume Trend</h2>
        {sourceControl}
        <p className="mt-2 text-xs text-destructive">
          Governed claims volume could not be loaded. No browser fixture was substituted.
        </p>
        <button
          type="button"
          className="mt-3 rounded-md border border-border px-3 py-1.5 text-xs hover:bg-muted"
          onClick={() => void query.refetch()}
        >
          Retry governed read
        </button>
      </section>
    );
  }

  const series = query.data.series;
  const hasAcceptedSource = series.some(({ status }) => status === "AVAILABLE");
  const total = series.find(({ metricId }) => metricId === "K002");
  const resolved = series.find(({ metricId }) => metricId === "K003");
  const trend = series.find(({ metricId }) => metricId === "K004");
  const forecast = series.find(({ metricId }) => metricId === "K005");
  const forecastLabel =
    forecast?.dataState === "SYNTHETIC" ? "Demonstration forecast" : "Governed forecast";
  const points = new Map<
    string,
    {
      periodStart: string;
      periodLabel: string;
      claims?: number;
      resolved?: number;
      predicted?: number;
    }
  >();
  const add = (
    source: ClaimsVolumeSeries | undefined,
    key: "claims" | "resolved" | "predicted",
  ) => {
    if (source?.status !== "AVAILABLE") return;
    for (const point of source.points) {
      const current = points.get(point.periodStart) ?? {
        periodStart: point.periodStart,
        periodLabel: claimsPeriodStartUtc(point.periodStart),
      };
      current[key] = point.value;
      points.set(point.periodStart, current);
    }
  };
  add(trend, "claims");
  add(resolved, "resolved");
  add(forecast, "predicted");
  const chartData = [...points.values()].sort((a, b) => a.periodStart.localeCompare(b.periodStart));
  const synthetic = series.some(({ dataState }) => dataState === "SYNTHETIC");
  const historical = series.some(
    ({ status, dataState, freshness }) =>
      status === "AVAILABLE" && dataState !== "SYNTHETIC" && freshness === "STALE",
  );

  return (
    <section
      aria-label="Claims volume trend"
      className="min-w-0 rounded-xl border border-border p-5 lg:col-span-2"
      style={{ background: "var(--gradient-card)", boxShadow: "var(--shadow-card)" }}
    >
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">Claims Volume Trend</h2>
          {sourceControl}
          <p className="text-xs text-muted-foreground">
            Governed canonical claim records · K002–K004 activation-gated
          </p>
          <p className="text-xs text-muted-foreground">
            Source period starts shown in UTC; distinct actual and forecast periods are not
            combined.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-[11px]">
          {[total, resolved, trend, forecast].map((item) => (
            <span key={item!.metricId} className={"rounded-full border px-2 py-0.5 " + tone(item)}>
              {item!.metricId} · {item!.dataState}
            </span>
          ))}
        </div>
      </div>

      {chartData.length === 0 ? (
        <div className="flex h-64 items-center justify-center rounded-lg border border-dashed border-border">
          <div className="max-w-md text-center">
            <div className="text-sm font-medium">No governed claims trend is available</div>
            <p className="mt-1 text-xs text-muted-foreground">
              Missing activation, scope, canonical lineage, or records remains unavailable. No
              fixture value was substituted.
            </p>
          </div>
        </div>
      ) : (
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ left: -10, right: 8, top: 8 }}>
              <defs>
                <linearGradient id="gGovernedClaims" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--color-primary)" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="var(--color-primary)" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gGovernedResolved" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--color-accent)" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="var(--color-accent)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="var(--color-border)" strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="periodStart"
                tickFormatter={claimsPeriodAxisUtc}
                minTickGap={24}
                stroke="var(--color-muted-foreground)"
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                stroke="var(--color-muted-foreground)"
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                labelFormatter={(value) => claimsPeriodStartUtc(String(value))}
                contentStyle={{
                  background: "var(--color-popover)",
                  border: "1px solid var(--color-border)",
                  borderRadius: 8,
                  fontSize: 12,
                }}
              />
              <Area
                type="monotone"
                dataKey="claims"
                name="Claims"
                stroke="var(--color-primary)"
                fill="url(#gGovernedClaims)"
                strokeWidth={2}
              />
              <Area
                type="monotone"
                dataKey="resolved"
                name="Resolved"
                stroke="var(--color-accent)"
                fill="url(#gGovernedResolved)"
                strokeWidth={2}
              />
              {forecast?.status === "AVAILABLE" && (
                <Line
                  type="monotone"
                  dataKey="predicted"
                  name={forecastLabel}
                  stroke="var(--color-muted-foreground)"
                  strokeDasharray="4 4"
                  strokeWidth={2}
                  dot={false}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
        <span>
          K002 total:{" "}
          {total?.status === "AVAILABLE" ? total.total?.toLocaleString() : "Unavailable"}
        </span>
        <span>
          K003 resolved:{" "}
          {resolved?.status === "AVAILABLE" ? resolved.total?.toLocaleString() : "Unavailable"}
        </span>
        <span>K005 forecast: {forecast?.status === "AVAILABLE" ? "Activated" : "Unavailable"}</span>
        <span>
          {hasAcceptedSource
            ? `As of ${new Date(query.data.dataAsOf).toLocaleString()}`
            : `No accepted evidence timestamp; response generated ${new Date(query.data.generatedAt).toLocaleString()}`}
        </span>
      </div>
      <div className="mt-2 text-[11px] text-muted-foreground">
        {trend?.sourceSummary} · {trend?.freshness.toLowerCase()} freshness ·{" "}
        {trend?.quality.toLowerCase()} quality · {trend?.reconciliation.toLowerCase()}{" "}
        reconciliation · {trend?.provenance.length ?? 0} lineage record
        {(trend?.provenance.length ?? 0) === 1 ? "" : "s"}
      </div>
      {forecast?.status === "AVAILABLE" && (
        <p className="mt-2 text-[11px] text-muted-foreground">
          {forecastLabel}: {forecast.sourceSummary} · {forecast.authorityClass} · cutoff{" "}
          {new Date(forecast.dataCutoffAt!).toLocaleString()} · run {forecast.calculationRunId}
        </p>
      )}
      {synthetic && (
        <div className="mt-3 rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-warning">
          SYNTHETIC · DEMONSTRATION_ONLY · production decision use prohibited
        </div>
      )}
      {historical && (
        <div className="mt-3 rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-warning">
          HISTORICAL REAL DATA · useful for pattern discovery · not a view of current operations
        </div>
      )}
      {forecast?.status === "UNAVAILABLE" && (
        <p className="mt-2 text-[11px] text-muted-foreground">{forecast.unavailableReason}</p>
      )}
      {chartData.length > 0 && (
        <ChartDataTable
          chartTitle="Claims Volume Trend"
          columns={[
            { key: "periodLabel", label: "Period start (UTC)" },
            { key: "claims", label: "Claims", unit: "count", numeric: true },
            { key: "resolved", label: "Resolved", unit: "count", numeric: true },
            { key: "predicted", label: forecastLabel, unit: "count", numeric: true },
          ]}
          rows={chartData.map((point) => ({
            periodLabel: point.periodLabel,
            claims: point.claims ?? null,
            resolved: point.resolved ?? null,
            predicted: point.predicted ?? null,
          }))}
          summary={`${chartData.length} source period points. K002 total ${total?.status === "AVAILABLE" ? total.total?.toLocaleString() : "unavailable"}; K003 resolved ${resolved?.status === "AVAILABLE" ? resolved.total?.toLocaleString() : "unavailable"}.`}
          contextNote={`${trend?.sourceSummary ?? "Governed claims source"} · ${trend?.freshness.toLowerCase() ?? "unknown"} freshness · ${trend?.quality.toLowerCase() ?? "unknown"} quality · ${trend?.reconciliation.toLowerCase() ?? "unknown"} reconciliation. ${synthetic ? "SYNTHETIC · DEMONSTRATION_ONLY · production decision use prohibited." : ""}${historical ? " HISTORICAL REAL DATA · useful for pattern discovery; not a view of current operations." : ""} As of ${new Date(query.data.dataAsOf).toLocaleString()}.`}
        />
      )}
    </section>
  );
}
